import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import identityApi, {
  CurrentUserContextDTO,
  UserTenantDTO,
  TenantOnboardingRequest,
  TenantOnboardingResponse,
} from '../api/identity';
import { setTenantContext } from '../utils/axiosInterceptor';

interface TenantContextType {
  // State
  userContext: CurrentUserContextDTO | null;
  currentTenant: UserTenantDTO | null;
  tenants: UserTenantDTO[];
  loading: boolean;
  error: string | null;
  requiresOnboarding: boolean;
  isPlatformAdmin: boolean;
  realmRoles: string[];

  // Actions
  refreshContext: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  onboard: (request: TenantOnboardingRequest) => Promise<TenantOnboardingResponse>;
  leaveTenant: (tenantId: string) => Promise<void>;
  patchCurrentTenant: (patch: Partial<UserTenantDTO>) => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const { authenticated, keycloak, user, loading: authLoading } = useAuth();
  
  // Track if we've already initialized for this user session
  const initializedForUser = useRef<string | null>(null);

  const [userContext, setUserContext] = useState<CurrentUserContextDTO | null>(null);
  const [currentTenant, setCurrentTenant] = useState<UserTenantDTO | null>(null);
  const [tenants, setTenants] = useState<UserTenantDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requiresOnboarding, setRequiresOnboarding] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [realmRoles, setRealmRoles] = useState<string[]>([]);

  /**
   * Fetch the user's context from the identity service
   */
  const fetchUserContext = useCallback(async () => {
    const keycloakUserId = keycloak?.tokenParsed?.sub;
    
    if (!authenticated || !keycloakUserId) {
      setLoading(false);
      return;
    }

    // Prevent duplicate fetches for the same user
    if (initializedForUser.current === keycloakUserId) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const email = user?.email;
      const displayName = user?.firstName && user?.lastName 
        ? `${user.firstName} ${user.lastName}` 
        : user?.username;

      const context = await identityApi.getCurrentUserContext(
        keycloakUserId,
        email,
        displayName
      );

      setUserContext(context);
      setTenants(context.tenants);
      setCurrentTenant(context.currentTenant || null);
      setRequiresOnboarding(context.requiresOnboarding);
      setIsPlatformAdmin(context.platformAdmin);
      setRealmRoles(context.realmRoles || []);
      
      // Mark this user as initialized
      initializedForUser.current = keycloakUserId;

      console.log('User context loaded:', {
        keycloakUserId,
        requiresOnboarding: context.requiresOnboarding,
        isPlatformAdmin: context.platformAdmin,
        realmRoles: context.realmRoles,
        tenantCount: context.tenants.length,
        currentTenant: context.currentTenant?.tenantId,
      });
    } catch (err) {
      console.error('Failed to fetch user context:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch user context');
      // On error, assume onboarding might be needed (fail open for new users)
      setRequiresOnboarding(true);
    } finally {
      setLoading(false);
    }
  }, [authenticated, keycloak?.tokenParsed?.sub, user?.email, user?.firstName, user?.lastName, user?.username]);

  /**
   * Refresh the user context
   */
  const refreshContext = useCallback(async () => {
    // Clear the initialization flag to allow refetch
    initializedForUser.current = null;
    await fetchUserContext();
  }, [fetchUserContext]);

  /**
   * Switch to a different tenant
   */
  const switchTenant = useCallback(async (tenantId: string) => {
    if (!keycloak?.tokenParsed?.sub) {
      throw new Error('User not authenticated');
    }

    try {
      const updatedMembership = await identityApi.switchTenant(
        keycloak.tokenParsed.sub,
        tenantId
      );

      // Update local state
      setCurrentTenant(updatedMembership);
      setTenants(prev =>
        prev.map(t => ({
          ...t,
          isDefault: t.tenantId === tenantId,
        }))
      );

      console.log('Switched to tenant:', tenantId);
    } catch (err) {
      console.error('Failed to switch tenant:', err);
      throw err;
    }
  }, [keycloak]);

  /**
   * Onboard a new user with their organization
   */
  const onboard = useCallback(async (request: TenantOnboardingRequest): Promise<TenantOnboardingResponse> => {
    if (!keycloak?.tokenParsed?.sub) {
      throw new Error('User not authenticated');
    }

    try {
      const keycloakUserId = keycloak.tokenParsed.sub;
      const email = user?.email;
      const displayName = user?.firstName && user?.lastName 
        ? `${user.firstName} ${user.lastName}` 
        : user?.username;

      const response = await identityApi.onboardUser(
        keycloakUserId,
        email,
        displayName,
        request
      );

      // Update local state with the new tenant
      setCurrentTenant(response.userTenant);
      setTenants([response.userTenant]);
      setRequiresOnboarding(false);
      setUserContext(prev => prev ? {
        ...prev,
        currentTenant: response.userTenant,
        tenants: [response.userTenant],
        requiresOnboarding: false,
      } : null);

      console.log('User onboarded successfully:', response.tenant.tenantId);
      return response;
    } catch (err) {
      console.error('Failed to onboard user:', err);
      throw err;
    }
  }, [keycloak, user]);

  /**
   * Patch current tenant state immediately (e.g. after a name update) without a full re-fetch
   */
  const patchCurrentTenant = useCallback((patch: Partial<UserTenantDTO>) => {
    setCurrentTenant(prev => prev ? { ...prev, ...patch } : prev);
    setTenants(prev =>
      prev.map(t =>
        t.tenantId === (patch.tenantId ?? currentTenant?.tenantId) ? { ...t, ...patch } : t
      )
    );
  }, [currentTenant?.tenantId]);

  /**
   * Leave a tenant
   */
  const leaveTenant = useCallback(async (tenantId: string) => {
    if (!keycloak?.tokenParsed?.sub) {
      throw new Error('User not authenticated');
    }

    try {
      await identityApi.leaveTenant(keycloak.tokenParsed.sub, tenantId);

      // Remove the tenant from local state
      const updatedTenants = tenants.filter(t => t.tenantId !== tenantId);
      setTenants(updatedTenants);

      // If we left the current tenant, switch to another one
      if (currentTenant?.tenantId === tenantId) {
        if (updatedTenants.length > 0) {
          setCurrentTenant(updatedTenants[0]);
        } else {
          setCurrentTenant(null);
          setRequiresOnboarding(true);
        }
      }

      console.log('Left tenant:', tenantId);
    } catch (err) {
      console.error('Failed to leave tenant:', err);
      throw err;
    }
  }, [keycloak, tenants, currentTenant]);

  // Fetch user context when authentication completes
  useEffect(() => {
    if (!authLoading && authenticated) {
      fetchUserContext();
    } else if (!authLoading && !authenticated) {
      // Reset state when logged out
      initializedForUser.current = null;
      setUserContext(null);
      setCurrentTenant(null);
      setTenants([]);
      setRequiresOnboarding(false);
      setIsPlatformAdmin(false);
      setRealmRoles([]);
      setLoading(false);
    }
  }, [authenticated, authLoading, fetchUserContext]);

  // Note: Workspace context syncing is now handled by WorkspaceContext
  // This only syncs tenant ID (workspace ID is set separately by WorkspaceProvider)
  useEffect(() => {
    if (currentTenant?.tenantId) {
      setTenantContext(currentTenant.tenantId, null); // WorkspaceContext will set workspaceId
      console.log('Synced tenant context to axios:', currentTenant.tenantId);
    } else {
      setTenantContext(null, null);
    }
  }, [currentTenant?.tenantId]);

  const value: TenantContextType = {
    userContext,
    currentTenant,
    tenants,
    loading,
    error,
    requiresOnboarding,
    isPlatformAdmin,
    realmRoles,
    refreshContext,
    switchTenant,
    onboard,
    leaveTenant,
    patchCurrentTenant,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export default TenantContext;
